import { useCallback, useMemo } from 'preact/hooks';
import useBotRequest from '@components/chatbots/twitch/data/useBotRequest.js';

export function useGetUserCampaign({
  twitchUsername,
  twitchAccessToken,
  twitchTokenValidated,
}) {
  const handleBotRequest = useBotRequest({ twitchUsername, twitchAccessToken });
  return useMemo(async () => {
    const url = import.meta.env.VITE_BOT_URL + '/getUserCampaign';

    if (!twitchAccessToken || !twitchTokenValidated)
      return { success: false, campaign: null };

    const { data: campaign, success } = await handleBotRequest(url);

    return { success, campaign };
  }, [handleBotRequest, twitchAccessToken, twitchTokenValidated]);
}

export function useSetUserCampaign({ config, twitchConfig, tiltifyConfig }) {
  return useCallback(
    (data = null) => {
      const url = import.meta.env.VITE_BOT_URL + '/writeUserCampaign';


      if (!data) {
        data = {
          twitchUsername: twitchConfig.twitchUsername,
          tiltifyCampaignName: tiltifyConfig.tiltifyCampaignName,
          tiltifyCampaignID: tiltifyConfig.tiltifyCampaignID,
          tiltifyCampaignSlug: tiltifyConfig.tiltifyCampaignSlug,
          tiltifyUserSlug: tiltifyConfig.tiltifyUsername,
          tiltifyUsername: tiltifyConfig.tiltifyUsername,
          donationTimer:
            config.donationOnOff === 'yes' ? config.timerOptions : 0,
          milestoneMessages: config.milestoneMessages === 'yes',
          isFake: false,
        };
      } else {
        data.donationTimer = config.donationOnOff === 'yes' ? config.timerOptions : 0;
      }

      const headers = new Headers();
      headers.set('Content-Type', 'application/json');
      headers.set('Authorization', twitchConfig.twitchAccessToken);

      return fetch(url, {
        method: 'POST',
        body: JSON.stringify(data),
        headers,
      })
        .then((response) => {
          if (response.status === 200) return response.json();
          return null;
        })
        .catch(() => null)
        .then((result) => {
          return result !== null;
        });
    },
    [
      twitchConfig.twitchAccessToken,
      twitchConfig.twitchUsername,
      tiltifyConfig.tiltifyCampaignName,
      tiltifyConfig.tiltifyCampaignID,
      tiltifyConfig.tiltifyCampaignSlug,
      tiltifyConfig.tiltifyUsername,
      config.donationOnOff,
      config.timerOptions,
      config.milestoneMessages,
    ]
  );
}

export function useRemoveUserCampaign(twitchConfig) {
  return useCallback(() => {
    const url = import.meta.env.VITE_BOT_URL + '/deleteUserCampaign';
    const data = {
      twitchUsername: twitchConfig.twitchUsername,
    };

    const headers = new Headers();
    headers.set('Content-Type', 'application/json');
    headers.set('Authorization', twitchConfig.twitchAccessToken);

    return fetch(url, {
      method: 'POST',
      body: JSON.stringify(data),
      headers,
    })
      .then((response) => {
        if (response.status === 200) return response.json();
        return false;
      })
      .catch(() => false)
      .then((result) => !!result);
  }, [twitchConfig.twitchAccessToken, twitchConfig.twitchUsername]);
}

export function useUpdateTiltifyConfigFromCampaign(
  onCampaignSelected,
  tiltifyConfig
) {
  return useCallback(
    (campaign) => {
      const { tiltifyCampaignSlug, tiltifyCampaignID, tiltifyCampaignName } =
        campaign;

      if (tiltifyConfig.tiltifyCampaignSlug !== tiltifyCampaignSlug) {
        tiltifyConfig.tiltifyCampaignSlug = tiltifyCampaignSlug;
        localStorage.setItem(
          'tiltifyCampaignSlug',
          tiltifyConfig.tiltifyCampaignSlug
        );
        onCampaignSelected({ campaignSlug: campaign['tiltifyCampaignSlug'] });
      }

      if (tiltifyConfig.tiltifyCampaignID !== tiltifyCampaignID) {
        tiltifyConfig.tiltifyCampaignID = tiltifyCampaignID;
        localStorage.setItem(
          'tiltifyCampaignID',
          tiltifyConfig.tiltifyCampaignID
        );
      }

      if (tiltifyConfig.tiltifyCampaignName !== tiltifyCampaignName) {
        tiltifyConfig.tiltifyCampaignName = tiltifyCampaignName;
        localStorage.setItem(
          'tiltifyCampaignName',
          tiltifyConfig.tiltifyCampaignName
        );
      }
    },
    [onCampaignSelected, tiltifyConfig]
  );
}
